import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Chip,
  makeStyles,
} from "@material-ui/core";
import {
  WarningRounded as WarningAmberIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@material-ui/icons";

interface Permission {
  id: number;
  title: string;
  description: string;
}

interface SubscriptionOverlapModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onUpgrade?: () => void;  // Phase 3: 자동 업그레이드 핸들러
  overlapData: {
    overlap_type: "exact" | "upgrade" | "downgrade" | "partial" | "independent";
    should_allow: boolean;
    message: string;
    requested_products: string[];
    overlapping_permissions: Permission[];
    new_permissions: Permission[];
    source?: 'frontend' | 'backend'; // 차단 출처 (프론트 vs 백엔드)
  } | null;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    position: "absolute",
    width: "90%",
    maxWidth: 600,
    maxHeight: "90vh",
    overflow: "auto",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[24],
    padding: theme.spacing(4),
    outline: "none",
    [theme.breakpoints.down("sm")]: {
      width: "95%",
      padding: theme.spacing(2),
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  icon: {
    fontSize: 48,
    marginRight: theme.spacing(2),
  },
  alert: {
    padding: theme.spacing(2),
    borderRadius: 4,
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  permissionBox: {
    padding: theme.spacing(1.5),
    borderRadius: 4,
    border: "1px solid",
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
}));

const SubscriptionOverlapModal: React.FC<SubscriptionOverlapModalProps> = ({
  open,
  onClose,
  onConfirm,
  onUpgrade,
  overlapData,
}) => {
  const classes = useStyles();

  if (!overlapData) return null;

  const {
    overlap_type,
    should_allow,
    message,
    requested_products,
    overlapping_permissions,
    new_permissions,
    source,
  } = overlapData;

  // 겹침 유형별 아이콘 및 색상
  const getOverlapTypeInfo = () => {
    switch (overlap_type) {
      case "exact":
        return {
          icon: <ErrorIcon className={classes.icon} style={{ color: "#f44336" }} />,
          color: "#f44336",
          bgColor: "#ffebee",
          title: "중복 구독",
        };
      case "downgrade":
        return {
          icon: <WarningAmberIcon className={classes.icon} style={{ color: "#ff9800" }} />,
          color: "#ff9800",
          bgColor: "#fff3e0",
          title: "다운그레이드 불가",
        };
      case "upgrade":
        return {
          icon: <CheckCircleIcon className={classes.icon} style={{ color: "#4caf50" }} />,
          color: "#4caf50",
          bgColor: "#e8f5e9",
          title: "업그레이드 구독",
        };
      case "partial":
        return {
          icon: <WarningAmberIcon className={classes.icon} style={{ color: "#2196f3" }} />,
          color: "#2196f3",
          bgColor: "#e3f2fd",
          title: "부분 겹침",
        };
      case "independent":
        return {
          icon: <CheckCircleIcon className={classes.icon} style={{ color: "#4caf50" }} />,
          color: "#4caf50",
          bgColor: "#e8f5e9",
          title: "독립 상품",
        };
      default:
        return {
          icon: <WarningAmberIcon className={classes.icon} style={{ color: "#757575" }} />,
          color: "#757575",
          bgColor: "#f5f5f5",
          title: "확인 필요",
        };
    }
  };

  const typeInfo = getOverlapTypeInfo();

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modal}
      aria-labelledby="subscription-overlap-modal-title"
      aria-describedby="subscription-overlap-modal-description"
    >
      <Box className={classes.paper}>
        {/* 헤더 */}
        <div className={classes.header}>
          {typeInfo.icon}
          <Typography
            id="subscription-overlap-modal-title"
            variant="h5"
            component="h2"
            style={{ fontWeight: "bold", color: typeInfo.color }}
          >
            {typeInfo.title}
          </Typography>
        </div>

        {/* 메시지 */}
        <div
          className={classes.alert}
          style={{
            backgroundColor: typeInfo.bgColor,
            borderLeft: `4px solid ${typeInfo.color}`,
          }}
        >
          <Typography variant="body1">{message}</Typography>
        </div>

        {/* 요청한 상품 */}
        {requested_products && requested_products.length > 0 && (
          <div className={classes.section}>
            <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 8 }}>
              요청한 상품
            </Typography>
            <div className={classes.chipContainer}>
              {requested_products.map((product, index) => (
                <Chip
                  key={index}
                  label={product}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </div>
          </div>
        )}

        {/* 겹치는 권한 */}
        {overlapping_permissions.length > 0 && (
          <div className={classes.section}>
            <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 8 }}>
              {source === 'frontend' ? '선택한 패키지에 포함된 권한' : '이미 구독 중인 권한'}
            </Typography>
            {overlapping_permissions.map((perm) => (
              <div
                key={perm.id}
                className={classes.permissionBox}
                style={{
                  backgroundColor: "#fff3e0",
                  borderColor: "#ffb74d",
                }}
              >
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {perm.title}
                </Typography>
                {perm.description && (
                  <Typography variant="body2" color="textSecondary">
                    {perm.description}
                  </Typography>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 새로 추가되는 권한 */}
        {new_permissions.length > 0 && (
          <div className={classes.section}>
            <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 8 }}>
              새로 추가되는 권한
            </Typography>
            {new_permissions.map((perm) => (
              <div
                key={perm.id}
                className={classes.permissionBox}
                style={{
                  backgroundColor: "#e8f5e9",
                  borderColor: "#81c784",
                }}
              >
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {perm.title}
                </Typography>
                {perm.description && (
                  <Typography variant="body2" color="textSecondary">
                    {perm.description}
                  </Typography>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 버튼 */}
        <div className={classes.buttonContainer}>
          {should_allow ? (
            overlap_type === "upgrade" && onUpgrade ? (
              <>
                <Button variant="outlined" onClick={onClose}>
                  취소
                </Button>
                <Button
                  variant="contained"
                  onClick={onUpgrade}
                  style={{ backgroundColor: typeInfo.color, color: "#fff" }}
                >
                  자동 업그레이드
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" onClick={onClose}>
                  취소
                </Button>
                <Button
                  variant="contained"
                  onClick={onConfirm}
                  style={{ backgroundColor: typeInfo.color, color: "#fff" }}
                >
                  계속 진행
                </Button>
              </>
            )
          ) : (
            <Button variant="contained" onClick={onClose} color="secondary">
              확인
            </Button>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default SubscriptionOverlapModal;
